import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EMPTY, ReplaySubject, catchError, finalize, take, takeUntil, tap, timer } from 'rxjs';

import { MfaHttpService } from '@core/http/mfa.http.service';
import {
  MFA_ERROR_CODES,
  MfaActionResultMap,
  MfaChallengeAction,
  MfaInitiateOpts,
  MfaValidateOpts,
  UpdateMyselfPayload,
} from '@interfaces';

type MfaActionVerificationStep = 'CODE' | 'RESULT';

interface MfaActionVerificationModalActionConfig {
  introTitleKey: string;
  codeStepDescriptionKey: string;
  resultTitleKey: string;
  resultDescriptionKey: string;
  invalidMetadataErrorKey: string;
}

export interface MfaActionVerificationModalData<TAction extends MfaChallengeAction = MfaChallengeAction> {
  action: TAction;
  metadata?: Record<string, unknown>;
  phoneNumber?: string | null;
}

export type MfaActionVerificationModalDismissedResult<TAction extends MfaChallengeAction = MfaChallengeAction> = {
  action: TAction;
  actionResult?: undefined;
  dismissed: true;
  success: false;
};

export type MfaActionVerificationModalSuccessResult<TAction extends MfaChallengeAction = MfaChallengeAction> = {
  action: TAction;
  actionResult: MfaActionResultMap[TAction];
  dismissed: false;
  success: true;
};

export type MfaActionVerificationModalResult<TAction extends MfaChallengeAction = MfaChallengeAction> =
  | MfaActionVerificationModalDismissedResult<TAction>
  | MfaActionVerificationModalSuccessResult<TAction>;

const MFA_ACTION_VERIFICATION_CONFIG: Partial<Record<MfaChallengeAction, MfaActionVerificationModalActionConfig>> = {
  [MfaChallengeAction.UPDATE_MY_SETTINGS]: {
    introTitleKey: 'MFA_ACTION_VERIFICATION_MODAL.ACTIONS.UPDATE_MY_SETTINGS.TITLE',
    codeStepDescriptionKey: 'MFA_ACTION_VERIFICATION_MODAL.ACTIONS.UPDATE_MY_SETTINGS.DESCRIPTION',
    resultTitleKey: 'MFA_ACTION_VERIFICATION_MODAL.ACTIONS.UPDATE_MY_SETTINGS.RESULT.TITLE',
    resultDescriptionKey: 'MFA_ACTION_VERIFICATION_MODAL.ACTIONS.UPDATE_MY_SETTINGS.RESULT.DESCRIPTION',
    invalidMetadataErrorKey: 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.INVALID_UPDATE_MY_SETTINGS_PAYLOAD',
  },
};

@Component({
  selector: 'app-mfa-action-verification-modal',
  templateUrl: './mfa-action-verification-modal.component.html',
  styleUrls: ['./mfa-action-verification-modal.component.scss'],
  standalone: false,
})
export class MfaActionVerificationModalComponent implements OnInit, OnDestroy {
  public readonly data = inject<MfaActionVerificationModalData>(MAT_DIALOG_DATA);
  public readonly dialogRef = inject(
    MatDialogRef<MfaActionVerificationModalComponent, MfaActionVerificationModalResult>
  );
  private readonly fb = inject(FormBuilder);
  private readonly mfaHttpService = inject(MfaHttpService);

  public currentStep: MfaActionVerificationStep = 'CODE';
  public loading = false;
  public codeErrorMessageKey: string | null = null;
  public requestMessageKey: string | null = null;
  public requestMessageTone: 'error' | 'success' | null = null;
  public resendCooldownSeconds = 0;
  public resultTitleKey: string | null = null;
  public resultDescriptionKey: string | null = null;
  public closeResult: MfaActionVerificationModalResult;

  public codeForm: FormGroup<any> = this.fb.group({
    code: [null, Validators.compose([Validators.required, Validators.pattern(/^\d{6}$/)])],
  });

  private readonly destroy$ = new ReplaySubject<boolean>(1);
  public readonly actionConfig = MFA_ACTION_VERIFICATION_CONFIG[this.data.action] ?? null;

  constructor() {
    this.closeResult = {
      action: this.data.action,
      dismissed: true,
      success: false,
    };
  }

  public ngOnInit(): void {
    if (!this.actionConfig || !this.hasValidMetadata()) {
      this.requestMessageKey =
        this.actionConfig?.invalidMetadataErrorKey ?? 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.UNSUPPORTED_ACTION';
      this.requestMessageTone = 'error';
      return;
    }

    this.sendChallenge(false);
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public close(): void {
    if (this.loading) {
      return;
    }

    this.dialogRef.close(this.closeResult);
  }

  public confirm(): void {
    if (this.currentStep === 'RESULT') {
      this.close();
      return;
    }

    this.submitCode();
  }

  public resendCode(): void {
    if (this.loading || this.resendCooldownSeconds > 0 || !this.actionConfig) {
      return;
    }

    this.sendChallenge(true);
  }

  private submitCode(): void {
    if (this.codeForm.invalid) {
      this.codeForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.codeErrorMessageKey = null;
    this.requestMessageKey = null;
    this.requestMessageTone = null;

    const opts: MfaValidateOpts = {
      action: this.data.action,
      code: this.codeForm.get('code')?.value,
    };

    this.mfaHttpService
      .validateChallenge(opts)
      .pipe(
        take(1),
        tap(response => {
          if (response?.success) {
            this.showResultStep(response?.result);
            return;
          }

          this.codeErrorMessageKey = 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.INVALID_CODE';
        }),
        catchError((error: HttpErrorResponse) => {
          this.codeErrorMessageKey = this.resolveCodeErrorMessageKey(error);
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe();
  }

  private sendChallenge(isResend: boolean): void {
    this.loading = true;
    this.codeErrorMessageKey = null;
    this.requestMessageKey = null;
    this.requestMessageTone = null;
    this.startResendCooldown();

    const opts: MfaInitiateOpts = {
      action: this.data.action,
      metadata: this.data.metadata,
    };

    this.mfaHttpService
      .initiateChallenge(opts)
      .pipe(
        take(1),
        tap(response => {
          if (response?.smsSent) {
            if (isResend) {
              this.requestMessageKey = 'MFA_ACTION_VERIFICATION_MODAL.MESSAGES.CODE_SENT_AGAIN';
              this.requestMessageTone = 'success';
            }
            return;
          }

          this.requestMessageKey = 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.SMS_SEND_FAILED';
          this.requestMessageTone = 'error';
        }),
        catchError((error: HttpErrorResponse) => {
          this.requestMessageKey = this.resolveInitiateErrorMessageKey(error);
          this.requestMessageTone = 'error';
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe();
  }

  private showResultStep(actionResult: MfaActionResultMap[MfaChallengeAction]): void {
    if (!this.actionConfig) {
      this.codeErrorMessageKey = 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.UNSUPPORTED_ACTION';
      return;
    }

    this.currentStep = 'RESULT';
    this.resultTitleKey = this.actionConfig.resultTitleKey;
    this.resultDescriptionKey = this.actionConfig.resultDescriptionKey;
    this.closeResult = {
      action: this.data.action,
      actionResult,
      dismissed: false,
      success: true,
    };
  }

  private hasValidMetadata(): boolean {
    if (!this.actionConfig) {
      return false;
    }

    if (this.data.action !== MfaChallengeAction.UPDATE_MY_SETTINGS) {
      return false;
    }

    const payload = this.data.metadata?.payload as UpdateMyselfPayload | undefined;
    return !!payload && typeof payload === 'object' && Object.keys(payload).length > 0;
  }

  private resolveInitiateErrorMessageKey(error: HttpErrorResponse): string {
    if (error?.status === 429) {
      return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.REQUEST_LIMIT_EXCEEDED';
    }

    switch (this.getErrorCode(error)) {
      case MFA_ERROR_CODES.invalidActionMetadata:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.INVALID_ACTION_METADATA';
      case MFA_ERROR_CODES.invalidPhoneNumber:
      case MFA_ERROR_CODES.userHasNoPhoneNumber:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.PHONE_NUMBER_REQUIRED';
      case MFA_ERROR_CODES.unsupportedAction:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.UNSUPPORTED_ACTION';
      default:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.SMS_SEND_FAILED';
    }
  }

  private resolveCodeErrorMessageKey(error: HttpErrorResponse): string {
    if (error?.status === 429) {
      return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.REQUEST_LIMIT_EXCEEDED';
    }

    switch (this.getErrorCode(error)) {
      case MFA_ERROR_CODES.challengeAttemptsExceeded:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.ATTEMPTS_EXCEEDED';
      case MFA_ERROR_CODES.challengeExpired:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.CHALLENGE_EXPIRED';
      case MFA_ERROR_CODES.challengeInvalidCode:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.INVALID_CODE';
      case MFA_ERROR_CODES.challengeNotFound:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.CHALLENGE_NOT_FOUND';
      case MFA_ERROR_CODES.invalidActionMetadata:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.INVALID_ACTION_METADATA';
      case MFA_ERROR_CODES.unsupportedAction:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.UNSUPPORTED_ACTION';
      default:
        return 'MFA_ACTION_VERIFICATION_MODAL.ERRORS.VALIDATION_FAILED';
    }
  }

  private getErrorCode(error: HttpErrorResponse): string | null {
    if (typeof error?.error?.message === 'string') {
      return error.error.message;
    }

    if (typeof error?.error?.code === 'string') {
      return error.error.code;
    }

    if (typeof error?.error === 'string') {
      return error.error;
    }

    return null;
  }

  private startResendCooldown(): void {
    this.resendCooldownSeconds = 60;

    timer(1000, 1000)
      .pipe(takeUntil(this.destroy$), take(this.resendCooldownSeconds))
      .subscribe(() => {
        this.resendCooldownSeconds = Math.max(this.resendCooldownSeconds - 1, 0);
      });
  }
}
